import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAgentProfile } from '@/hooks/useAgentProfile';
import { ArrowRight, ArrowLeft, Check } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { ProductPicker } from './ProductPicker';
import { useAuth } from '@/contexts/AuthContext';
import { addManyToTechStack } from '@/data/techStack';

interface AgentGoals {
  volume_target: number;
  business_mix: 'buyers' | 'sellers' | 'balanced' | 'investors';
  niche: string[];
  lead_strategy: 'referral' | 'leads' | 'sphere' | 'team' | 'mixed';
  work_style: 'go_getter' | 'relationship' | 'analytical' | 'team_player';
  growth_path: 'stay_solo' | 'build_team' | 'join_team' | 'scale_existing';
}

interface OnboardingData {
  deals_closed_last_year?: number;
  average_deal_size?: number;
  years_in_real_estate?: number;
  career_stage?: string;
  has_team?: boolean;
  team_size?: number;
  primary_market?: string;
  budget_range?: string;
  pain_points?: string[];
  current_tools?: string[];
  goals?: AgentGoals;
}

interface AgentOnboardingProps {
  onComplete: () => void;
}

export function AgentOnboarding({ onComplete }: AgentOnboardingProps) {
  const { updateProfile, isUpdating } = useAgentProfile();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [data, setData] = useState<OnboardingData>({});
  const [selectedProducts, setSelectedProducts] = useState<any[]>([]);

  const updateData = (field: string, value: any) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (step < 3) setStep(step + 1);
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const handleComplete = async () => {
    try {
      // Update profile with structured goals
      await updateProfile({
        deals_closed_last_year: data.deals_closed_last_year,
        average_deal_size: data.average_deal_size,
        years_in_real_estate: data.years_in_real_estate,
        career_stage: data.career_stage as 'new_agent' | 'growing' | 'established' | 'team_leader' | 'top_producer',
        has_team: data.has_team,
        team_size: data.team_size,
        primary_market: data.primary_market,
        budget_range: data.budget_range as 'low' | 'mid' | 'high',
        pain_points: data.pain_points,
        current_tools: data.current_tools,
        goals: data.goals ? [data.goals] : [],
        onboarding_completed: true,
      });

      // Save selected products to tech stack
      if (selectedProducts.length > 0 && user?.id) {
        await addManyToTechStack(
          user.id,
          selectedProducts.map(p => ({ productId: p.id, status: 'owned' }))
        );
      }

      toast({
        title: 'Profile completed!',
        description: 'Let\'s find the perfect tools for you.',
      });
      onComplete();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save profile. Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="max-w-2xl mx-auto p-6">
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          {[1, 2, 3].map(s => (
            <div
              key={s}
              className={`flex-1 h-2 rounded-full mx-1 transition-colors ${
                s <= step ? 'bg-primary' : 'bg-muted'
              }`}
            />
          ))}
        </div>
        <p className="text-sm text-muted-foreground text-center">
          Step {step} of 3
        </p>
      </div>

      <Card className="p-8">
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Experience & Volume</h2>
              <p className="text-muted-foreground">
                Help us understand where you are in your career
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label>How many closings in the last 12 months?</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    type="button"
                    variant={data.deals_closed_last_year === 3 ? 'default' : 'outline'}
                    onClick={() => updateData('deals_closed_last_year', 3)}
                  >
                    0-6
                  </Button>
                  <Button
                    type="button"
                    variant={data.deals_closed_last_year === 10 ? 'default' : 'outline'}
                    onClick={() => updateData('deals_closed_last_year', 10)}
                  >
                    7-12
                  </Button>
                  <Button
                    type="button"
                    variant={data.deals_closed_last_year === 18 ? 'default' : 'outline'}
                    onClick={() => updateData('deals_closed_last_year', 18)}
                  >
                    13-24
                  </Button>
                  <Button
                    type="button"
                    variant={data.deals_closed_last_year === 30 ? 'default' : 'outline'}
                    onClick={() => updateData('deals_closed_last_year', 30)}
                  >
                    25+
                  </Button>
                </div>
              </div>

              <div>
                <Label>Typical sale price</Label>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Button
                    type="button"
                    variant={data.average_deal_size === 200000 ? 'default' : 'outline'}
                    onClick={() => updateData('average_deal_size', 200000)}
                  >
                    Under $250k
                  </Button>
                  <Button
                    type="button"
                    variant={data.average_deal_size === 375000 ? 'default' : 'outline'}
                    onClick={() => updateData('average_deal_size', 375000)}
                  >
                    $250k-$500k
                  </Button>
                  <Button
                    type="button"
                    variant={data.average_deal_size === 750000 ? 'default' : 'outline'}
                    onClick={() => updateData('average_deal_size', 750000)}
                  >
                    $500k-$1M
                  </Button>
                  <Button
                    type="button"
                    variant={data.average_deal_size === 1500000 ? 'default' : 'outline'}
                    onClick={() => updateData('average_deal_size', 1500000)}
                  >
                    $1M+
                  </Button>
                </div>
              </div>

              <div>
                <Label>Primary focus</Label>
                <Select
                  value={data.primary_market}
                  onValueChange={v => updateData('primary_market', v)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select focus" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="buyers">Buyers</SelectItem>
                    <SelectItem value="sellers">Sellers</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                    <SelectItem value="investors">Investors</SelectItem>
                    <SelectItem value="luxury">Luxury</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="stage">Career Stage</Label>
                <Select
                  value={data.career_stage}
                  onValueChange={v => updateData('career_stage', v)}
                >
                  <SelectTrigger id="stage">
                    <SelectValue placeholder="Select your stage" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new_agent">New Agent (0-2 years)</SelectItem>
                    <SelectItem value="growing">Growing (2-5 years)</SelectItem>
                    <SelectItem value="established">Established (5-10 years)</SelectItem>
                    <SelectItem value="team_leader">Team Leader</SelectItem>
                    <SelectItem value="top_producer">Top Producer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Do you have a team?</Label>
                <div className="flex gap-4">
                  <Button
                    type="button"
                    variant={data.has_team === true ? 'default' : 'outline'}
                    onClick={() => updateData('has_team', true)}
                  >
                    Yes
                  </Button>
                  <Button
                    type="button"
                    variant={data.has_team === false ? 'default' : 'outline'}
                    onClick={() => updateData('has_team', false)}
                  >
                    No
                  </Button>
                </div>
              </div>

              {data.has_team && (
                <div>
                  <Label htmlFor="teamSize">Team Size</Label>
                  <Input
                    id="teamSize"
                    type="number"
                    min="1"
                    value={data.team_size || ''}
                    onChange={e => updateData('team_size', parseInt(e.target.value))}
                    placeholder="e.g., 4"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Tech Stack & Budget</h2>
              <p className="text-muted-foreground">
                What tools do you currently use? This helps us avoid suggesting tools you already have.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <Label htmlFor="market">Primary Market</Label>
                <Input
                  id="market"
                  value={data.primary_market || ''}
                  onChange={e => updateData('primary_market', e.target.value)}
                  placeholder="e.g., Austin, TX"
                />
              </div>

              <div>
                <Label>Current Tools You Own/Use</Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Search and select the tools from our marketplace
                </p>
                <ProductPicker
                  selectedProducts={selectedProducts}
                  onToggleProduct={(product) => {
                    setSelectedProducts(prev => {
                      const exists = prev.some(p => p.id === product.id);
                      if (exists) {
                        return prev.filter(p => p.id !== product.id);
                      }
                      return [...prev, product];
                    });
                  }}
                />
              </div>

              <div>
                <Label htmlFor="budget">Monthly Budget Range</Label>
                <Select
                  value={data.budget_range}
                  onValueChange={v => updateData('budget_range', v)}
                >
                  <SelectTrigger id="budget">
                    <SelectValue placeholder="Select budget range" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low ($0-$100/mo)</SelectItem>
                    <SelectItem value="mid">Mid ($100-$300/mo)</SelectItem>
                    <SelectItem value="high">High ($300+/mo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold mb-2">Your Business Goals</h2>
              <p className="text-muted-foreground">
                Help us find the perfect tools for your unique situation
              </p>
            </div>

            <div className="space-y-6">
              {/* Volume Target */}
              <div className="space-y-2">
                <Label>How many deals do you want to close next year?</Label>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-muted-foreground w-20">
                    {data.deals_closed_last_year || 0} now
                  </span>
                  <Input
                    type="range"
                    min={Math.max(1, (data.deals_closed_last_year || 0) - 5)}
                    max={(data.deals_closed_last_year || 10) + 50}
                    step={1}
                    value={data.goals?.volume_target || data.deals_closed_last_year || 10}
                    onChange={e => updateData('goals', { 
                      ...data.goals, 
                      volume_target: parseInt(e.target.value) 
                    })}
                    className="flex-1"
                  />
                  <span className="text-lg font-semibold w-20 text-right">
                    {data.goals?.volume_target || data.deals_closed_last_year || 10} goal
                  </span>
                </div>
              </div>

              {/* Business Mix */}
              <div className="space-y-2">
                <Label>What's your buyer/seller split?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={data.goals?.business_mix === 'buyers' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, business_mix: 'buyers' })}
                  >
                    Mostly Buyers
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.business_mix === 'sellers' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, business_mix: 'sellers' })}
                  >
                    Mostly Sellers
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.business_mix === 'balanced' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, business_mix: 'balanced' })}
                  >
                    50/50 Balanced
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.business_mix === 'investors' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, business_mix: 'investors' })}
                  >
                    Investors
                  </Button>
                </div>
              </div>

              {/* Niche Multi-Select */}
              <div className="space-y-2">
                <Label>What type of business do you want to focus on?</Label>
                <p className="text-xs text-muted-foreground">Select all that apply</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'luxury', label: 'Luxury' },
                    { value: 'investment', label: 'Investment' },
                    { value: 'new_construction', label: 'New Construction' },
                    { value: 'first_time', label: 'First-Time Buyers' },
                    { value: 'relocation', label: 'Relocation' },
                  ].map(niche => (
                    <Button
                      key={niche.value}
                      type="button"
                      variant={data.goals?.niche?.includes(niche.value) ? 'default' : 'outline'}
                      onClick={() => {
                        const currentNiches = data.goals?.niche || [];
                        const newNiches = currentNiches.includes(niche.value)
                          ? currentNiches.filter(n => n !== niche.value)
                          : [...currentNiches, niche.value];
                        updateData('goals', { ...data.goals, niche: newNiches });
                      }}
                      className="justify-start"
                    >
                      {niche.label}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Lead Strategy */}
              <div className="space-y-2">
                <Label>How do you want to get business?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={data.goals?.lead_strategy === 'referral' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, lead_strategy: 'referral' })}
                  >
                    Referrals/Past Clients
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.lead_strategy === 'leads' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, lead_strategy: 'leads' })}
                  >
                    Paid Leads
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.lead_strategy === 'sphere' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, lead_strategy: 'sphere' })}
                  >
                    Sphere/Networking
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.lead_strategy === 'team' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, lead_strategy: 'team' })}
                  >
                    Part of a Team
                  </Button>
                </div>
              </div>

              {/* Work Style */}
              <div className="space-y-2">
                <Label>What describes you best?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={data.goals?.work_style === 'go_getter' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, work_style: 'go_getter' })}
                  >
                    Hustler/Go-Getter
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.work_style === 'relationship' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, work_style: 'relationship' })}
                  >
                    Relationship Builder
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.work_style === 'analytical' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, work_style: 'analytical' })}
                  >
                    Systems/Analytical
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.work_style === 'team_player' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, work_style: 'team_player' })}
                  >
                    Team Player
                  </Button>
                </div>
              </div>

              {/* Growth Path */}
              <div className="space-y-2">
                <Label>Where are you headed?</Label>
                <div className="grid grid-cols-2 gap-2">
                  <Button
                    type="button"
                    variant={data.goals?.growth_path === 'stay_solo' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, growth_path: 'stay_solo' })}
                  >
                    Stay Solo
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.growth_path === 'build_team' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, growth_path: 'build_team' })}
                  >
                    Build a Team
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.growth_path === 'join_team' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, growth_path: 'join_team' })}
                  >
                    Join a Team
                  </Button>
                  <Button
                    type="button"
                    variant={data.goals?.growth_path === 'scale_existing' ? 'default' : 'outline'}
                    onClick={() => updateData('goals', { ...data.goals, growth_path: 'scale_existing' })}
                  >
                    Scale Existing Team
                  </Button>
                </div>
              </div>

              {/* Pain Points */}
              <div className="space-y-2">
                <Label htmlFor="pain">Top Pain Points (comma-separated)</Label>
                <Textarea
                  id="pain"
                  value={data.pain_points?.join(', ') || ''}
                  onChange={e =>
                    updateData(
                      'pain_points',
                      e.target.value.split(',').map(t => t.trim()).filter(Boolean)
                    )
                  }
                  placeholder="e.g., lead generation, time management, client communication"
                  rows={2}
                />
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-between mt-8">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={step === 1}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>

          {step < 3 ? (
            <Button onClick={handleNext}>
              Next
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={handleComplete} disabled={isUpdating}>
              {isUpdating ? (
                'Saving...'
              ) : (
                <>
                  Complete
                  <Check className="ml-2 h-4 w-4" />
                </>
              )}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
